import * as React from 'react';
import {
  Grid,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Button,
  LinearProgress,
  Divider,
  Box,
  FormControl,
} from '@mui/material';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { graphql, useFragment } from '../../../__generated__';
import UserSearch from '../users/UserSearch';
import CurrencySelect from './CurrencySelect';
import {
  getCurrencyLabelSymbol,
  getReceiveAmount,
  getSendAndReceivedMonies,
  isRequestEditable,
} from 'gmd-utilities-package';
import { getMinMaxRateChange } from '../../../util/satellites';
import { transferOptions } from '../../../util/options';
import { getSendAndReceiveAmounts } from 'gmd-utilities-package';
import { User } from '../../../types';
import {
  newRequestState,
  currentRequest,
  duplicatedTransaction,
} from '../common';
import useCommonNavigations from '../../../hooks/Navigation';
import {
  TransactionLimit,
  getLimitForPhone,
  validatePhone,
} from 'gmd-utilities-package';
import { RequestFormCss } from './styles';
import { displayMoney } from 'gmd-utilities-package';
import { AgentRole } from '../../../__generated__/graphql';

export const transactionFormQuery = graphql(/* GraphQL */ `
  query GetTransactionFormData {
    tenant {
      id
      currency
      maxBonus
      rateChangeThreshold1
      rateChangeThreshold2
      rateChangeThreshold3
      rates {
        code
        source
        destination
        rate
      }
    }
    agent {
      role
      limit
    }
    account {
      balance
    }
  }
`);

export const TransactionFormFragment = graphql(/* GraphQL */ `
  fragment TransactionFormTransaction on Transaction {
    id
    sender {
      id
      name
      phone
    }
    recipient {
      id
      name
      phone
    }
    sent {
      amount
      currency
    }
    received {
      amount
      currency
    }
    exchangeRate
    purpose
    isBonus
    status
    sendMode
    requestCount
    historicals {
      senderName
      recipientName
    }
    bill {
      billNumber
      amount
      date
      dueDate
      customerNumber
      merchant
      payItemDesc
      payItemId
      serviceId
      serviceName
      serviceNumber
    }
  }
`);

export const transactionQuery = graphql(/* GraphQL */ `
  query GetTransaction($transactionId: ID!) {
    transaction(id: $transactionId) {
      ...TransactionFormTransaction
    }
  }
`);

const sendOptions = ['SEND', 'RECEIVE'];
const cssStyles = RequestFormCss();
const asNumber = (value: string) => (!Number.isNaN(value) ? +value : 0);

const TransactionForm = () => {
  const { id: editedTransactionId } = useParams();
  const {
    data: trxData,
    loading: trxLoading,
    error: trxError,
  } = useQuery(transactionQuery, {
    variables: {
      transactionId: editedTransactionId || '',
    },
    skip: !editedTransactionId,
  });
  const {
    data,
    loading: formLoading,
    error: formDataError,
  } = useQuery(transactionFormQuery);
  const { backToBookmark } = useCommonNavigations();

  const [sender, setSender] = React.useState<User | null>(null);
  const [recipient, setRecipient] = React.useState<User | null>(null);
  const [isBonus, setIsBonus] = React.useState(false);
  const [sendOption, setSendOption] = React.useState('SEND');
  const [srcCurrencies, setSrcCurrencies] = React.useState<string[]>([]);
  const [destCurrencies, setDestCurrencies] = React.useState<string[]>([]);
  const [ratesMap, setRatesMap] = React.useState<{ [key: string]: number }>({});
  const [srcCurrency, setSrcCurrency] = React.useState<string>('');
  const [destCurrency, setDestCurrency] = React.useState<string>('');
  const [exchangeRate, setExchangeRate] = React.useState<string>('');
  const [purpose, setPurpose] = React.useState('OTHER');
  const [amount, setAmount] = React.useState<string | number>('');
  const [formError, setFormError] = React.useState('');
  const [sendRateLimit, setSendRateLimit] =
    React.useState<TransactionLimit | null>();
  const [rateChangeError, setRateChangeError] = React.useState<string>('');

  const req = currentRequest();
  const duplicatedTrx = useReactiveVar(duplicatedTransaction);
  const trx = useFragment(
    TransactionFormFragment,
    editedTransactionId ? trxData?.transaction : duplicatedTrx
  );

  React.useEffect(() => {
    if (trx && !req) {
      setSender(trx.sender || null);
      setRecipient(trx.recipient || null);
      setIsBonus(trx.isBonus || false);
      setSendOption(trx.sendMode);
      setSrcCurrency(trx.sent.currency);
      setDestCurrency(trx.received.currency);
      setExchangeRate(trx.exchangeRate?.toString());
      setPurpose(trx.purpose);
      setAmount(
        trx.sendMode === 'SEND' ? trx.sent.amount : trx.received.amount
      );
      duplicatedTransaction(null);
    }
  }, [trx, req]);

  React.useEffect(() => {
    if (req) {
      setSender(req.sender || null);
      setRecipient(req.recipient || null);
      setIsBonus(req.isBonus || false);
      setSendOption(req.sendMode);
      setSrcCurrency(req.sent.currency);
      setDestCurrency(req.received.currency);
      setExchangeRate(req.exchangeRate.toString());
      setPurpose(req.purpose);
      setAmount(
        req.sendMode === 'SEND' ? req.sent.amount : req.received.amount
      );
    }
  }, [req]);

  const onChangeDestCurrency = React.useCallback(
    (currency: string) => {
      setDestCurrency(currency);
      //update the exchange rage
      if (srcCurrency && currency) {
        const xchange = ratesMap[`${srcCurrency}.${currency}`];
        setExchangeRate(xchange.toString());
      }
    },
    [ratesMap, srcCurrency]
  );

  React.useEffect(() => {
    const sc = new Set<string>();
    const dc = new Set<string>();
    const rm: { [key: string]: number } = {};
    const rates = data?.tenant?.rates || [];
    rates.forEach((rate) => {
      if (rate !== null) {
        sc.add(rate.source);
        dc.add(rate.destination);
        rm[`${rate.source}.${rate.destination}`] = rate.rate;
      }
    });
    setSrcCurrencies(Array.from(sc));
    setDestCurrencies(Array.from(dc));
    setRatesMap(rm);
  }, [data?.tenant?.rates]);

  React.useEffect(() => {
    if (srcCurrencies.length > 0 && !srcCurrency) {
      setSrcCurrency(srcCurrencies[0]);
    }
  }, [srcCurrencies, srcCurrency]);

  React.useEffect(() => {
    if (destCurrencies.length > 0 && !destCurrency) {
      let currValue: string | undefined = destCurrencies.find(
        (value) => value === 'xaf'
      );
      if (!currValue) currValue = destCurrencies[0];
      onChangeDestCurrency(currValue);
    }
  }, [destCurrencies, destCurrency, onChangeDestCurrency]);

  React.useEffect(() => {
    if (srcCurrency && destCurrency && Object.keys(ratesMap).length > 0) {
      setExchangeRate(ratesMap[`${srcCurrency}.${destCurrency}`].toString());
    }
  }, [destCurrency, ratesMap, srcCurrency]);

  const localAmount = React.useMemo(() => {
    let amountError = false;
    if (typeof amount === 'number') {
      let maxDisplayMsg = '';
      let minDisplayMsg = '';
      const receiveAmount =
        sendOption === 'SEND'
          ? getReceiveAmount(amount, asNumber(exchangeRate))
          : amount;
      let sendLimits: TransactionLimit | null = null;
      if (recipient?.phone) {
        sendLimits = getLimitForPhone(recipient?.phone);
        setSendRateLimit(sendLimits);
        if (sendLimits?.maxAmount) {
          maxDisplayMsg = `Maximum transaction amount is ${displayMoney(
            sendLimits?.maxAmount,
            destCurrency.toUpperCase(),
            true
          )} for ${sendLimits?.rateName}`;
        }
        if (sendLimits?.minAmount) {
          minDisplayMsg = `Minimum transaction amount is ${displayMoney(
            sendLimits?.minAmount,
            destCurrency.toUpperCase(),
            true
          )} for ${sendLimits?.rateName}`;
        }
      }
      amountError =
        sendLimits && +receiveAmount > sendLimits?.maxAmount
          ? true
          : false || (sendLimits && +receiveAmount < sendLimits?.minAmount)
          ? true
          : false;
      return {
        receiveAmount: +receiveAmount,
        maxDisplayMsg,
        minDisplayMsg,
        amountError,
      };
    } else {
      return {
        receiveAmount: 0,
        maxDisplayMsg: '',
        minDisplayMsg: '',
        amountError,
      };
    }
  }, [amount, sendOption, exchangeRate, destCurrency, recipient?.phone]);
  React.useEffect(() => {
    if (destCurrency && recipient && recipient !== null) {
      //validate currency
      const error = validatePhone(recipient.phone, destCurrency);
      setFormError(error);
    }
  }, [destCurrency, recipient, setFormError]);

  const onSendOptionChange = (e: { target: { value: string } }) => {
    setSendOption(e.target.value);
    setAmount('');
  };
  const onAmountChange = (e: { target: { value: string } }) => {
    const val = e.target.value || '';
    if (val.length === 0) {
      setAmount('');
    } else {
      setAmount(parseFloat(val));
    }
  };

  const agent = data?.agent;
  const tenant = data?.tenant;
  const rates = React.useMemo(
    () => data?.tenant?.rates || [],
    [data?.tenant?.rates]
  );
  const onXchChange = (e: { target: { value: string } }) => {
    const val = e.target.value || '';
    if (val.length === 0) {
      setExchangeRate('');
    } /* else if (agent?.role !== 'satellite' || isBonus) {
      
      setExchangeRate(val);
    } */ else {
      //const defaultRateCode = rates.find(
      //  (r) => r?.code === `${srcCurrency}-${destCurrency}`
      //);
      //const defaultRate = defaultRateCode?.rate || 0;
      const newRate = asNumber(val);
      setExchangeRate(val);

      if (
        rateChangeInfo.canChangeRate &&
        rateChangeInfo.min <= newRate &&
        rateChangeInfo.max >= newRate
      ) {
        setRateChangeError('');
      } else {
        setRateChangeError(
          `Rate change has to be between ${rateChangeInfo.min} and ${rateChangeInfo.max}`
        );
      }

      //if (canChangeRate(defaultRate, asNumber(val), rateChangeInfo.changePct)) {
      //  setExchangeRate(val);
      //}
    }
  };
  const onPurposeChange = (e: { target: { value: string } }) =>
    setPurpose(e.target.value);

  const onBonusChange = (e: { target: { checked: boolean } }) => {
    setIsBonus(e.target.checked);
  };

  const maxBonusInfo = React.useMemo(() => {
    if (isBonus) {
      const result = getSendAndReceiveAmounts(
        tenant?.maxBonus || 0,
        srcCurrency,
        destCurrency,
        asNumber(exchangeRate),
        sendOption
      );
      return result;
    } else {
      return {
        sendAmount: 0,
        receiveAmount: 0,
      };
    }
  }, [
    isBonus,
    tenant?.maxBonus,
    srcCurrency,
    destCurrency,
    exchangeRate,
    sendOption,
  ]);

  const rateChangeInfo = React.useMemo(() => {
    let changePct = 100;

    if (agent?.role !== AgentRole.Super) {
      if (agent?.role === AgentRole.Admin) {
        changePct = tenant?.rateChangeThreshold1 || 0;
      } else if (agent?.role === AgentRole.Agent) {
        changePct = tenant?.rateChangeThreshold2 || 0;
      } else if (agent?.role === AgentRole.Satellite) {
        changePct = tenant?.rateChangeThreshold3 || 0;
      }
    }

    const defaultRateCode = rates.find(
      (r) => r?.code === `${srcCurrency}-${destCurrency}`
    );
    const defaultRate = defaultRateCode?.rate || 0;

    const rateChangeInfo = getMinMaxRateChange(defaultRate, changePct);

    const result = { ...rateChangeInfo, changePct };

    return result;
  }, [
    srcCurrency,
    destCurrency,
    rates,
    tenant?.rateChangeThreshold1,
    tenant?.rateChangeThreshold2,
    tenant?.rateChangeThreshold3,
    agent?.role,
  ]);

  const handleCancel = () => {
    duplicatedTransaction(null);
    currentRequest(null);
    backToBookmark('REQUESTS');
  };

  const loading = formLoading || trxLoading;
  const error =
    tenant?.id === 'SystemTenant'
      ? 'Please go to Settings and choose a client/tenant.'
      : formDataError || trxError;
  const editable =
    tenant?.id !== 'SystemTenant' &&
    !loading &&
    (trx?.status ? isRequestEditable(trx?.status) : true);

  const canSubmit = () => {
    let bonusOk = true;
    let rateChangeOk = true;

    const maxBonus = tenant?.maxBonus || 0;
    const amt: number = amount === '' ? 0 : +amount;
    if (isBonus) {
      bonusOk = amt <= maxBonus;
    }
    if (rateChangeError) {
      rateChangeOk = false;
    }
    return (
      !formError &&
      // !submitting && // TODO
      !loading &&
      editable &&
      sender !== null &&
      recipient !== null &&
      amt > 0 &&
      !localAmount.amountError &&
      // agentEdit &&  // TODO
      asNumber(exchangeRate) > 0 &&
      rateChangeOk &&
      bonusOk
    );
  };

  const editMode =
    editedTransactionId !== undefined &&
    editedTransactionId &&
    editedTransactionId.length > 0;

  const handleAction = () => {
    if (sender === null) {
      setFormError('Missing sender information.');
      return;
    }
    if (recipient === null) {
      setFormError('Missing recipient information.');
      return;
    }

    const { sent, received } = getSendAndReceivedMonies(
      parseInt(`${amount || 0}`),
      srcCurrency,
      destCurrency,
      asNumber(exchangeRate),
      sendOption
    );
    if (editMode) {
      // set the current request
      currentRequest({
        id: trx?.id || '',
        sender,
        recipient,
        sent,
        received,
        exchangeRate: asNumber(exchangeRate),
        purpose: purpose || '',
        isBonus,
        sendMode: sendOption,
        status: trx?.status || '',
        transactionType: 'TRANSFER',
      });
    } else {
      // set the current request
      currentRequest({
        sender,
        recipient,
        sent,
        received,
        exchangeRate: asNumber(exchangeRate),
        purpose: purpose || '',
        isBonus,
        sendMode: sendOption,
        status: 'CREATING',
        transactionType: 'TRANSFER',
      });
    }
    // change pages
    newRequestState('review');
  };

  const agentRole = data?.agent?.role;
  const agentBalance = data?.account?.balance || 0;
  const agentLimit = data?.agent?.limit || 0;
  const currentCurrency = React.useMemo(() => {
    return sendOption === 'SEND' ? srcCurrency : destCurrency;
  }, [destCurrency, sendOption, srcCurrency]);

  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Transaction Details
      </Typography>
      {agentRole === 'satellite' && agentLimit && agentLimit > 0 && (
        <Typography variant="subtitle1" color="primary" gutterBottom>
          {`$${agentBalance} of your $${agentLimit} limit is available for use.`}
        </Typography>
      )}
      {loading && <LinearProgress />}
      {
        <Typography
          variant="caption"
          gutterBottom
          color={formError || error ? 'red' : 'CaptionText'}
        >
          {`${formError ? formError : error ? error : '* Required'}`}
        </Typography>
      }
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          {sender && (
            <UserSearch
              type="sender"
              srcCurrency={tenant?.currency || srcCurrency}
              destCurrency={destCurrency}
              onUserChanged={(user: User | null) => {
                setSender(user);
                //Only reset the recipient if the sender changed
                if (user?.id !== sender.id) {
                  setRecipient(null);
                }
              }}
              label="Sender"
              disabled={!editable}
              user={sender}
            />
          )}
          {!sender && (
            <UserSearch
              type="sender"
              srcCurrency={tenant?.currency || srcCurrency}
              destCurrency={destCurrency}
              onUserChanged={(user: User | null) => {
                setSender(user);
                setRecipient(null);
              }}
              label="Sender"
              disabled={!editable}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {recipient && (
            <UserSearch
              type="recipient"
              sender={sender?.id}
              srcCurrency={tenant?.currency || srcCurrency}
              destCurrency={destCurrency}
              onUserChanged={(user: User | null) => {
                setRecipient(user);
              }}
              label="Recipient"
              disabled={!editable}
              user={recipient}
            />
          )}
          {!recipient && (
            <UserSearch
              type="recipient"
              sender={sender?.id}
              srcCurrency={tenant?.currency || srcCurrency}
              destCurrency={destCurrency}
              onUserChanged={(user: User | null) => {
                setRecipient(user);
              }}
              label="Recipient"
              disabled={!editable}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <CurrencySelect
            id="from"
            label="Send Currency"
            codes={srcCurrencies}
            value={srcCurrency}
            onChange={setSrcCurrency}
            disabled={!editable || srcCurrencies.length < 2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CurrencySelect
            id="to"
            label="Recipient Currency"
            codes={destCurrencies}
            value={destCurrency}
            onChange={onChangeDestCurrency}
            disabled={!editable || destCurrencies.length < 2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id="sendOption"
            select
            label="Send Option"
            variant="standard"
            value={sendOption}
            onChange={onSendOptionChange}
            fullWidth
            disabled={loading || !editable}
            sx={{ pt: 1, pb: 1 }}
          >
            {sendOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ pt: 2, pb: 2 }}>
                {option === 'SEND'
                  ? 'Amount to be sent'
                  : 'Amount to be received'}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              margin="dense"
              id="amount"
              label="Amount to transfer"
              value={amount}
              onChange={onAmountChange}
              type="number"
              fullWidth
              required
              variant="standard"
              error={localAmount && localAmount?.amountError}
              disabled={loading || !editable}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {getCurrencyLabelSymbol(currentCurrency?.toUpperCase())}
                  </InputAdornment>
                ),
              }}
            />
            {sendRateLimit &&
              localAmount?.receiveAmount > sendRateLimit?.maxAmount && (
                <Typography
                  variant="subtitle2"
                  sx={{ ...cssStyles.hintText, ...cssStyles.redText }}
                >
                  {localAmount.maxDisplayMsg}
                </Typography>
              )}
            {sendRateLimit &&
              localAmount?.receiveAmount < sendRateLimit?.minAmount && (
                <Typography
                  variant="subtitle2"
                  sx={{ ...cssStyles.hintText, ...cssStyles.redText }}
                >
                  {localAmount.minDisplayMsg}
                </Typography>
              )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id="xch"
            label="Exchange Rate"
            variant="standard"
            fullWidth
            value={exchangeRate}
            onChange={onXchChange}
            name="xch"
            type="number"
            disabled={loading || !editable || !rateChangeInfo.canChangeRate}
            // sx={{ mt: 1 }}
          />
          {rateChangeError && (
            <Typography
              variant="subtitle2"
              sx={{ fontSize: 12, color: 'error.main' }}
            >
              {rateChangeError}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id="purpose"
            select
            label="Purpose"
            variant="standard"
            value={purpose}
            onChange={onPurposeChange}
            fullWidth
            disabled={loading || !editable}
            // sx={{ mt: 1 }}
          >
            {transferOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox checked={isBonus} onChange={onBonusChange} />}
            label="Mark as customer bonus"
            disabled={loading || !editable}
          />
          {isBonus && (
            <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
              Max Bonus is {maxBonusInfo.receiveAmount} or{' '}
              {maxBonusInfo.sendAmount}
            </Typography>
          )}
        </Grid>
        {/* <Grid item xs={12} sm={6}>

        </Grid> */}
      </Grid>
      <Divider sx={{ mt: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel} disabled={loading} sx={{ mt: 3, ml: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAction}
          disabled={!canSubmit()}
          sx={{ mt: 3, ml: 1 }}
        >
          {editMode === false ? 'Next' : 'Save'}
        </Button>
      </Box>
    </React.Fragment>
  );
};

export default TransactionForm;
